import { SyntheticEvent, useContext } from 'react';
import { useApolloClient } from '@apollo/client';

import { ErrorAlert } from './styled/HeaderJoy.styled';
import { StyledATag } from './styled/ActionablesJoy.styled';
import { StyledHR } from './styled/StaticElementsJoy.styled';
import { Typography, Box, Stack } from '@mui/joy';
import HeaderJoyComponentCircle from './HeaderJoyCircle.component';
import { LanguageSwitcher } from './LanguageSwitcher.tsx';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DeleteHoldDocument } from '../../graphql/graphql.ts';
import { CartContext } from '../providers/cart.tsx';
import { A1CheckoutCartObject } from '../../types/common.ts';

import * as Sentry from '@sentry/react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
    previousPage: string;
    step: number;
    errorAlert?: string | null;
    hold?: A1CheckoutCartObject['hold'];
    setErrorAlert?: (err: string | null) => void;
    organizerPrimaryColor: string;
    organizerSecondaryColor?: string;
    headerTitle?: string;
    hasAdditionalTerms: boolean;
    hasQuestionnaire: boolean;
}

export default function HeaderJoyComponent({
    previousPage,
    errorAlert = null,
    setErrorAlert = () => {},
    step,
    hold,
    organizerPrimaryColor,
    headerTitle,
    hasAdditionalTerms,
    hasQuestionnaire,
}: HeaderProps) {
    const { t } = useTranslation();
    const { currentEventId, resetCart } = useContext(CartContext);
    const apollo = useApolloClient();

    const checkStep = async (e: SyntheticEvent, previousPage: string) => {
        e.preventDefault();

        if (step === 2 && !hasAdditionalTerms) {
            if (hold?.id) {
                try {
                    await apollo.mutate({
                        mutation: DeleteHoldDocument,
                        variables: {
                            deleteHoldId: hold?.id,
                            addToInventory: true,
                        },
                    });
                } catch (err) {
                    console.error(err);
                    Sentry.captureException(err);
                } finally {
                    if (currentEventId) {
                        resetCart(currentEventId);

                        window.location.href = previousPage;
                    }
                }
            }
        }

        if (step === 3 && !hasQuestionnaire && hasAdditionalTerms) {
            if (hold?.id) {
                try {
                    await apollo.mutate({
                        mutation: DeleteHoldDocument,
                        variables: {
                            deleteHoldId: hold?.id,
                            addToInventory: true,
                        },
                    });
                } catch (err) {
                    console.error(err);
                    Sentry.captureException(err);
                } finally {
                    if (currentEventId) {
                        resetCart(currentEventId);

                        window.location.href = previousPage;
                    }
                }
            }
        }

        if (step === 4 && hasQuestionnaire && hasAdditionalTerms) {
            if (hold?.id) {
                try {
                    await apollo.mutate({
                        mutation: DeleteHoldDocument,
                        variables: {
                            deleteHoldId: hold?.id,
                            addToInventory: true,
                        },
                    });
                } catch (err) {
                    console.error(err);
                    Sentry.captureException(err);
                } finally {
                    if (currentEventId) {
                        resetCart(currentEventId);

                        window.location.href = previousPage;
                    }
                }
            }
        } else {
            window.location.href = previousPage;
        }
    };

    const checkoutStep = () => {
        if (hasQuestionnaire && hasAdditionalTerms) {
            return 4;
        } else if (hasQuestionnaire && !hasAdditionalTerms) {
            return 3;
        } else if (!hasQuestionnaire && hasAdditionalTerms) {
            return 3;
        } else {
            return 2;
        }
    };

    const checkoutStepChecked = () => {
        if (hasQuestionnaire && hasAdditionalTerms) {
            return step >= 5;
        } else if (hasQuestionnaire && !hasAdditionalTerms) {
            return step >= 4;
        } else if (!hasQuestionnaire && hasAdditionalTerms) {
            return step >= 4;
        } else {
            return step >= 3;
        }
    };

    const confirmationStep = () => {
        if (hasQuestionnaire && hasAdditionalTerms) {
            return 5;
        } else if (hasQuestionnaire && !hasAdditionalTerms) {
            return 4;
        } else if (!hasQuestionnaire && hasAdditionalTerms) {
            return 4;
        } else {
            return 3;
        }
    };

    const isMobile = useMediaQuery('(max-width: 450px)');

    const turnOffText = isMobile && hasAdditionalTerms && hasQuestionnaire;

    return (
        <header>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <StyledATag
                    href={previousPage}
                    setcolor={organizerPrimaryColor}
                    onClick={(e) => checkStep(e, previousPage)}
                >
                    <Typography
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: '600',
                            color: organizerPrimaryColor,
                        }}
                    >
                        <ArrowBackIosNewIcon sx={{ fontSize: 14, mr: 1, strokeWidth: 6 }} />
                        {t('header.backToPreviousPage', 'Back To Previous Page')}
                    </Typography>
                </StyledATag>
                <LanguageSwitcher color="dark" />
            </Box>
            {errorAlert && (
                <ErrorAlert>
                    <Box sx={{ display: 'flex' }}>
                        <ErrorOutlineIcon sx={{ marginRight: '10px' }} />
                        <Stack>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    margin: '0 0 10px 0',
                                    color: '#fff',
                                    fontWeight: 700,
                                }}
                            >
                                {t('header.transactionFailed', 'Transaction Failed')}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#fff' }}>
                                {errorAlert}
                            </Typography>
                        </Stack>
                    </Box>
                    <CloseIcon onClick={() => setErrorAlert(null)} />
                </ErrorAlert>
            )}
            <Box sx={{ mb: 3, display: 'flex', alignItems: { md: 'center', xs: 'flex-start' } }}>
                <HeaderJoyComponentCircle
                    stepId={1}
                    checked={step >= 2}
                    minWidthMD="140px"
                    minWidthSM={
                        (hasAdditionalTerms || hasQuestionnaire) && !turnOffText
                            ? '80px'
                            : turnOffText
                            ? 'auto'
                            : '100px'
                    }
                    text={turnOffText ? '' : t('header.steps.orderSummary', 'Order Summary')}
                    organizerColor={organizerPrimaryColor}
                    current={step}
                />

                <StyledHR />
                {hasQuestionnaire && (
                    <>
                        <HeaderJoyComponentCircle
                            stepId={2}
                            checked={hasAdditionalTerms ? step >= 3 : step >= 2}
                            minWidthMD="150px"
                            minWidthSM={
                                (hasAdditionalTerms || hasQuestionnaire) && !turnOffText
                                    ? '80px'
                                    : turnOffText
                                    ? 'auto'
                                    : '100px'
                            }
                            text={turnOffText ? '' : t('header.steps.questionnaire', 'Questionnaire')}
                            organizerColor={organizerPrimaryColor}
                            current={step}
                        />
                        <StyledHR />
                    </>
                )}

                {hasAdditionalTerms && (
                    <>
                        <HeaderJoyComponentCircle
                            stepId={hasQuestionnaire ? 3 : 2}
                            checked={hasQuestionnaire ? step >= 4 : step >= 3}
                            minWidthMD="150px"
                            minWidthSM={
                                (hasAdditionalTerms || hasQuestionnaire) && !turnOffText
                                    ? '80px'
                                    : turnOffText
                                    ? 'auto'
                                    : '100px'
                            }
                            text={turnOffText ? '' : t('header.steps.additionalTerms', 'Additional Terms')}
                            organizerColor={organizerPrimaryColor}
                            current={step}
                        />
                        <StyledHR />
                    </>
                )}

                <HeaderJoyComponentCircle
                    stepId={checkoutStep()}
                    checked={checkoutStepChecked()}
                    text={turnOffText ? '' : t('header.steps.checkout', 'Checkout')}
                    organizerColor={organizerPrimaryColor}
                    current={step}
                />

                <StyledHR />

                <HeaderJoyComponentCircle
                    stepId={confirmationStep()}
                    checked={checkoutStepChecked()}
                    text={turnOffText ? '' : t('header.steps.confirmation', 'Confirmation')}
                    organizerColor={organizerPrimaryColor}
                    current={step}
                />
            </Box>
            <Typography
                component="h2"
                level="title-lg"
                sx={{
                    mb:
                        headerTitle === 'Questionnaire' || headerTitle === 'Additional Terms'
                            ? 0
                            : 3,
                    fontSize: 36,
                    textAlign: 'left',
                    fontWeight: 700,
                }}
            >
                {headerTitle}
            </Typography>
        </header>
    );
}
